// pages/WalletPage.jsx
// Token Wallet - UPDATED Dec 29: Now uses real tokenHistory GraphQL query
// UPDATED Dec 30: Passes tier props to TokenRedemption (fixes B14)
// UPDATED Dec 31: Passes onLearnMore to TokenRedemption (4.3 completion)
// UPDATED Dec 31: Added hybrid tooltips with AVA integration (4.9)
// UPDATED Jan 15, 2026: B40 - Fixed "New Member Member" duplication bug
// ============================================

import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  Plus, 
  Send, 
  Info, 
  ChevronLeft, 
  Coins,
  RefreshCw,
  Filter,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/common/BottomNav';
import TokenTransferModal from '../components/wallet/TokenTransferModal';
import TokenEconomyVisualization from '../components/wallet/TokenEconomyVisualization';
import TokenRedemption from '../components/wallet/TokenRedemption';
import { useTokens } from '../context/TokenContext';
import { formatTokens, calculateLoyaltyTier } from '../utils/tokenEconomy';
import Tooltip, { 
  TokenBalanceTooltip, 
  LoyaltyTierTooltip,
  TokenExpirationTooltip 
} from '../components/common/Tooltip';

// GraphQL query for current user (for tier calculation)
const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      name
      email
      loyaltyTier
      totalPurchases
      successfulReferrals
      wallet {
        tokenBalance
      }
    }
  }
`;

// GraphQL query for REAL token history from backend
const TOKEN_HISTORY = gql`
  query TokenHistory($limit: Int, $offset: Int, $type: TokenTransactionType, $direction: TokenDirection) {
    tokenHistory(limit: $limit, offset: $offset, type: $type, direction: $direction) {
      transactions {
        id
        type
        direction
        amount
        balanceAfter
        description
        createdAt
        metadata {
          purchaseId
          rating
          hasComment
        }
      }
      total
      limit
      offset
    }
  }
`;

// GraphQL query for earnings summary
const TOKEN_EARNINGS_SUMMARY = gql`
  query TokenEarningsSummary($days: Int) {
    tokenEarningsSummary(days: $days) {
      period
      earnings {
        purchases { total count }
        referrals { total count }
        jobs { total count }
        feedback { total count }
        total
      }
      spending {
        redemptions { total count }
        total
      }
      netChange
    }
  }
`;

const WalletPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('history');
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [showTokenEconomy, setShowTokenEconomy] = useState(false);
  const [filterDirection, setFilterDirection] = useState(null); // null = all, 'CREDIT', 'DEBIT'
  const [historyLimit, setHistoryLimit] = useState(20);
  
  const { balance, refreshBalance } = useTokens();
  
  // Fetch current user for tier data
  const { data: userData } = useQuery(GET_CURRENT_USER, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore'
  });
  
  const currentUser = userData?.me;
  
  // Calculate user's tier from their activity (or use stored tier)
  const userPurchases = currentUser?.totalPurchases || 0;
  const userReferrals = currentUser?.successfulReferrals || 0;
  const calculatedTier = calculateLoyaltyTier(userPurchases, userReferrals);
  const userTier = currentUser?.loyaltyTier || calculatedTier.tier || 'NEW_USER';
  
  // Fetch REAL token history from backend
  const { 
    data: historyData, 
    loading: historyLoading, 
    error: historyError,
    refetch: refetchHistory 
  } = useQuery(TOKEN_HISTORY, {
    variables: { 
      limit: historyLimit, 
      offset: 0,
      direction: filterDirection 
    },
    fetchPolicy: 'cache-and-network'
  });
  
  // Fetch earnings summary for the stats cards
  const { data: summaryData } = useQuery(TOKEN_EARNINGS_SUMMARY, {
    variables: { days: 30 },
    fetchPolicy: 'cache-and-network'
  });
  
  const transactions = historyData?.tokenHistory?.transactions || [];
  const totalTransactions = historyData?.tokenHistory?.total || 0;
  const summary = summaryData?.tokenEarningsSummary;
  
  const handleSendTokens = () => {
    setIsTransferModalOpen(true);
  };

  const handleTokenSend = (data) => {
    console.log('Sending tokens:', data);
    alert(`Sending ${data.amount} tokens to ${data.recipient}`);
  };

  const toggleTokenEconomy = () => {
    setShowTokenEconomy(!showTokenEconomy);
  };

  const handleRedemptionComplete = (data) => {
    console.log('Redemption completed:', data);
    // Refresh data after redemption
    refetchHistory();
    refreshBalance?.();
  };
  
  const handleRefresh = () => {
    refetchHistory();
    refreshBalance?.();
  };
  
  const handleLoadMore = () => {
    setHistoryLimit(prev => prev + 20);
  };

  // Format transaction type for display
  const formatTransactionType = (type) => {
    const typeMap = {
      'PURCHASE_REWARD': 'Purchase Reward',
      'PICKUP_BONUS': 'Pickup Bonus',
      'FIRST_PURCHASE_BONUS': 'First Purchase Bonus',
      'REFERRAL_REWARD': 'Referral Reward',
      'REFEREE_BONUS': 'Signup Bonus',
      'RECURRING_REFERRAL': 'Referral Earnings',
      'JOB_COMPLETION': 'Job Completed',
      'COORDINATOR_BONUS': 'Coordinator Bonus',
      'GROUP_SUCCESS_BONUS': 'Group Bonus',
      'LOYALTY_BONUS': 'Loyalty Reward',
      'PLATFORM_FEEDBACK': 'Feedback Reward',
      'ADMIN_CREDIT': 'Admin Credit',
      'CHECKOUT_REDEMPTION': 'Checkout Redemption',
      'ASSET_RENTAL': 'Asset Rental',
      'SERVICE_FEE': 'Service Fee',
      'ADMIN_DEBIT': 'Admin Adjustment'
    };
    return typeMap[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // B40 FIX: Helper function to format tier label without duplication
  const formatTierLabel = (label) => {
    if (!label) return 'Member';
    return label.includes('Member') ? label : `${label} Member`;
  };

  return (
    <div className="container mx-auto px-4 pb-20">
      <div className="mt-6 mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="mr-3 p-1 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold">Token Wallet</h1>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleRefresh}
            className="p-2 hover:bg-gray-100 rounded-full"
            title="Refresh"
          >
            <RefreshCw size={18} className={historyLoading ? 'animate-spin' : ''} />
          </button>
          <button 
            onClick={toggleTokenEconomy}
            className="flex items-center text-blue-600"
          >
            <Info size={16} className="mr-1" />
            <span className="text-sm">Token Info</span>
          </button>
        </div>
      </div>
      
      {/* Token Economy Explanation - Expandable */}
      {showTokenEconomy && (
        <div className="mb-6">
          <TokenEconomyVisualization 
            userPurchases={userPurchases}
            userReferrals={userReferrals}
            userTier={userTier}
          />
        </div>
      )}
      
      {/* Token Balance Card */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        {/* Balance Header with Tooltip */}
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-sm font-medium opacity-90">Your Balance</h2>
          {/* 4.9 TOOLTIP: Token Balance Help */}
          <TokenBalanceTooltip 
            balance={balance || 0} 
            className="opacity-75 hover:opacity-100"
          />
        </div>
        
        <div className="flex items-baseline">
          <span className="text-4xl font-bold">{formatTokens(balance || 0)}</span>
          <Coins className="w-8 h-8 text-yellow-300 ml-3" />
        </div>
        <p className="text-sm opacity-75 mt-1">
          ≈ ${((balance || 0) / 10).toFixed(2)} value
        </p>
        
        {/* Tier Badge with Tooltip - B40 FIX APPLIED */}
        <div className="mt-3 flex items-center gap-2">
          <div className="inline-flex items-center bg-white/20 backdrop-blur px-3 py-1 rounded-full">
            <span className="text-sm font-medium">{formatTierLabel(calculatedTier.label)}</span>
          </div>
          {/* 4.9 TOOLTIP: Loyalty Tier Help */}
          <LoyaltyTierTooltip 
            tier={userTier} 
            className="opacity-75 hover:opacity-100"
          />
        </div>
        
        <div className="flex mt-4 space-x-3">
          <button 
            className="flex-1 bg-white/20 backdrop-blur text-white py-2.5 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
            onClick={handleSendTokens}
          >
            <Send size={18} className="mr-2" />
            <span>Send</span>
          </button>
          <button 
            className="flex-1 bg-white text-blue-600 py-2.5 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            onClick={() => navigate('/earn')}
          >
            <div className="flex items-center justify-center">
              <Plus size={18} className="mr-1" />
              <span>Earn More</span>
            </div>
          </button>
        </div>
      </div>
      
      {/* Quick Stats - From Real Data */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Earned (30d)</p>
                <p className="text-xl font-bold text-green-700">+{formatTokens(summary.earnings.total)}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-green-600 mt-1">
              {summary.earnings.purchases.count + summary.earnings.referrals.count + summary.earnings.jobs.count} transactions
            </p>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Redeemed (30d)</p>
                <p className="text-xl font-bold text-orange-700">-{formatTokens(summary.spending.total)}</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-orange-600 mt-1">
              {summary.spending.redemptions.count} redemptions
            </p>
          </div>
        </div>
      )}
      
      {/* Token Redemption Section - NOW WITH TIER PROPS AND onLearnMore */}
      <TokenRedemption 
        balance={balance} 
        onComplete={handleRedemptionComplete}
        userPurchases={userPurchases}
        userReferrals={userReferrals}
        userTier={userTier}
        onLearnMore={() => {
          setShowTokenEconomy(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
      
      {/* Tabs & Filter */}
      <div className="flex items-center justify-between border-b mb-4 mt-6">
        <div className="flex">
          <button
            className={`px-4 py-2 ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
            onClick={() => setActiveTab('history')}
          >
            History ({totalTransactions})
          </button>
        </div>
        
        {/* Filter Dropdown */}
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select
            value={filterDirection || 'all'}
            onChange={(e) => setFilterDirection(e.target.value === 'all' ? null : e.target.value)}
            className="text-sm border-0 bg-transparent text-gray-600 focus:outline-none cursor-pointer"
          >
            <option value="all">All</option>
            <option value="CREDIT">Earned</option>
            <option value="DEBIT">Spent</option>
          </select>
        </div>
      </div>
      
      {/* Transaction History - REAL DATA */}
      <div className="space-y-3">
        {historyLoading && transactions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading transactions...</p>
          </div>
        ) : historyError ? (
          <div className="text-center py-8 bg-red-50 rounded-lg">
            <p className="text-red-600">Error loading transactions</p>
            <button 
              onClick={() => refetchHistory()}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Coins className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No transactions yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Start earning tokens by making purchases or completing jobs!
            </p>
            <button
              onClick={() => navigate('/earn')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Start Earning
            </button>
          </div>
        ) : (
          <>
            {transactions.map((tx) => {
              const isCredit = tx.direction === 'CREDIT';
              return (
                <div key={tx.id} className="bg-white rounded-lg shadow-sm border p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start">
                      <div className={`p-2 rounded-full mr-3 flex-shrink-0 ${
                        isCredit ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {isCredit ? (
                          <ArrowUpRight className="text-green-600" size={18} />
                        ) : (
                          <ArrowDownLeft className="text-red-600" size={18} />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {formatTransactionType(tx.type)}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(tx.createdAt)}
                        </p>
                        {tx.description && (
                          <p className="text-sm text-gray-600 mt-1">{tx.description}</p>
                        )}
                        {tx.metadata?.rating && (
                          <p className="text-xs text-yellow-600 mt-1">
                            ⭐ Rated {tx.metadata.rating}/5
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold text-lg ${
                        isCredit ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {isCredit ? '+' : '-'}{formatTokens(tx.amount)}
                      </span>
                      {tx.balanceAfter !== undefined && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Balance: {formatTokens(tx.balanceAfter)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Load More Button */}
            {transactions.length < totalTransactions && (
              <button
                onClick={handleLoadMore}
                className="w-full py-3 text-blue-600 font-medium text-sm hover:bg-blue-50 rounded-lg transition-colors"
              >
                Load More ({totalTransactions - transactions.length} remaining)
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Token Transfer Modal */}
      <TokenTransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onSend={handleTokenSend}
        maxAmount={balance}
      />
      
      <BottomNav />
    </div>
  );
};

export default WalletPage;